import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { contractsApi, ContractInvoiceItem } from '../../api/contractsApi';
import { formatVndAmount } from '../../utils/vndFormat';
import { formatDateToDisplay } from '../../utils/apartmentDate';
import ApartmentServiceFeePaymentModal from '../../components/modals/ApartmentServiceFeePaymentModal';
import { formatContractAmountField, parseContractAmountField } from '../../utils/contractAmount.util';
import { compareBillingPeriods, getInvoiceAmountAfterTax } from '../../utils/serviceFeeDebt';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { REVENUE_CONTRACT_PERMISSIONS } from '../../constants/functionPermissions';

const PAYMENT_STATUS_LABELS: Record<ContractInvoiceItem['paymentStatus'], string> = {
  PAID: 'Đã thanh toán',
  PARTIAL: 'Thanh toán một phần',
  UNPAID: 'Chưa thanh toán',
};

const PAYMENT_STATUS_CLASS: Record<ContractInvoiceItem['paymentStatus'], string> = {
  PAID: 'bg-emerald-100 text-emerald-700',
  PARTIAL: 'bg-amber-100 text-amber-800',
  UNPAID: 'bg-red-100 text-red-700',
};

type PaymentStatusFilter = '' | ContractInvoiceItem['paymentStatus'];

type InvoiceGroup = {
  key: string;
  contractCode: string;
  customerName: string | null;
  apartmentId: string;
  apartmentCode: string;
  billingPeriod: string;
  invoices: ContractInvoiceItem[];
};

const buildInvoiceGroups = (invoices: ContractInvoiceItem[]): InvoiceGroup[] => {
  const map = new Map<string, InvoiceGroup>();
  for (const invoice of invoices) {
    const key = `${invoice.contractId ?? invoice.contractCode ?? '—'}-${invoice.apartmentId}-${invoice.billingPeriod}`;
    const existing = map.get(key);
    if (existing) {
      existing.invoices.push(invoice);
    } else {
      map.set(key, {
        key,
        contractCode: invoice.contractCode ?? '—',
        customerName: invoice.customerName ?? null,
        apartmentId: invoice.apartmentId,
        apartmentCode: invoice.apartmentCode,
        billingPeriod: invoice.billingPeriod,
        invoices: [invoice],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => {
    const periodCmp = compareBillingPeriods(a.billingPeriod, b.billingPeriod);
    if (periodCmp !== 0) return periodCmp;
    const contractCmp = a.contractCode.localeCompare(b.contractCode, 'vi');
    if (contractCmp !== 0) return contractCmp;
    return a.apartmentCode.localeCompare(b.apartmentCode, 'vi');
  });
};

interface ContractInvoiceListTabProps {
  buildingId: string;
}

export const ContractInvoiceListTab: React.FC<ContractInvoiceListTabProps> = ({ buildingId }) => {
  const { canDelete: canDeleteContractInvoice } = useCrudPermissions({
    delete: REVENUE_CONTRACT_PERMISSIONS.deleteInvoice,
  });
  const [invoices, setInvoices] = useState<ContractInvoiceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [contractSearch, setContractSearch] = useState('');
  const [apartmentSearch, setApartmentSearch] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusFilter>('');
  const [appliedFilters, setAppliedFilters] = useState({
    contractSearch: '',
    apartmentSearch: '',
    billingPeriod: '',
    paymentStatus: '' as PaymentStatusFilter,
  });

  const [paymentModal, setPaymentModal] = useState<{
    apartmentId: string;
    apartmentCode: string;
    invoiceIds: string[];
    billingPeriod: string;
  } | null>(null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const [amountModal, setAmountModal] = useState<ContractInvoiceItem | null>(null);
  const [amountInput, setAmountInput] = useState('');
  const [savingAmount, setSavingAmount] = useState(false);

  const loadInvoices = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const result = await contractsApi.listContractInvoices(buildingId, {
        page,
        limit,
        contractSearch: appliedFilters.contractSearch.trim() || undefined,
        apartmentSearch: appliedFilters.apartmentSearch.trim() || undefined,
        billingPeriod: appliedFilters.billingPeriod.trim() || undefined,
        paymentStatus: appliedFilters.paymentStatus || undefined,
      });
      setInvoices(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      toast.error('Không thể tải danh sách hóa đơn');
      setInvoices([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [buildingId, page, appliedFilters]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    setPage(1);
    setAppliedFilters({
      contractSearch: '',
      apartmentSearch: '',
      billingPeriod: '',
      paymentStatus: '',
    });
    setContractSearch('');
    setApartmentSearch('');
    setBillingPeriod('');
    setPaymentStatus('');
  }, [buildingId]);

  const invoiceGroups = useMemo(() => buildInvoiceGroups(invoices), [invoices]);

  const contractInvoiceTotal = (invoice: ContractInvoiceItem) =>
    getInvoiceAmountAfterTax({
      amount: invoice.amount,
      baseAmount: invoice.baseAmount,
      vatAmount: invoice.vatAmount,
    });

  const needsManualAmount = (invoice: ContractInvoiceItem) =>
    contractInvoiceTotal(invoice) === 0 && invoice.paymentStatus !== 'PAID';

  const groupTotalAmount = (group: InvoiceGroup) =>
    group.invoices.reduce((sum, inv) => sum + contractInvoiceTotal(inv), 0);

  const groupPaidAmount = (group: InvoiceGroup) =>
    group.invoices.reduce((sum, inv) => sum + (inv.paidAmount ?? 0), 0);

  const groupPaymentStatus = (group: InvoiceGroup): ContractInvoiceItem['paymentStatus'] => {
    if (group.invoices.every((inv) => inv.paymentStatus === 'PAID')) return 'PAID';
    if (group.invoices.some((inv) => inv.paymentStatus === 'PARTIAL')) return 'PARTIAL';
    return 'UNPAID';
  };

  const groupUnpaidInvoiceIds = (group: InvoiceGroup) =>
    group.invoices
      .filter((inv) => inv.paymentStatus !== 'PAID' && !needsManualAmount(inv))
      .map((inv) => inv.id);

  const handleDeleteInvoice = async (invoice: ContractInvoiceItem) => {
    if (!canDeleteContractInvoice) {
      toast.error('Bạn không có quyền xóa hóa đơn hợp đồng thu');
      return;
    }
    if (!invoice.canDeleteWithoutReceipt) {
      toast.error('Hóa đơn đã có phiếu thu hoặc đã thanh toán một phần — không thể xóa tại đây');
      return;
    }
    const paidMarkerOnly = invoice.paymentStatus === 'PAID';
    const message = paidMarkerOnly
      ? `Xóa hóa đơn kỳ ${invoice.billingPeriod} (${invoice.contractCode})?\n\nTrạng thái Đã thanh toán chỉ từ đã thu trước HT — không có phiếu thu. Sau khi xóa, chạy Tạo HĐ lại để tạo lịch đúng.`
      : `Xóa hóa đơn kỳ ${invoice.billingPeriod} (${invoice.contractCode})?`;
    if (!window.confirm(message)) {
      return;
    }
    try {
      await contractsApi.deleteContractInvoice(invoice.id);
      toast.success('Đã xóa hóa đơn');
      loadInvoices();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Không thể xóa hóa đơn'));
    }
  };

  const groupCanDelete = (group: InvoiceGroup) =>
    canDeleteContractInvoice &&
    group.invoices.length > 0 &&
    group.invoices.every((inv) => inv.canDeleteWithoutReceipt);

  const openAmountModal = (invoice: ContractInvoiceItem) => {
    setAmountModal(invoice);
    const total = contractInvoiceTotal(invoice);
    setAmountInput(total > 0 ? formatContractAmountField(total) : '');
  };

  const closeAmountModal = () => {
    setAmountModal(null);
    setAmountInput('');
  };

  const saveInvoiceAmount = async () => {
    if (!amountModal) return;
    const amount = parseContractAmountField(amountInput);
    if (amount <= 0) {
      toast.error('Vui lòng nhập số tiền lớn hơn 0');
      return;
    }
    setSavingAmount(true);
    try {
      await contractsApi.updateContractInvoiceAmount(amountModal.id, { amount });
      toast.success('Đã cập nhật số tiền hóa đơn');
      closeAmountModal();
      loadInvoices();
    } catch {
      toast.error('Không thể cập nhật số tiền');
    } finally {
      setSavingAmount(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedFilters({
      contractSearch,
      apartmentSearch,
      billingPeriod,
      paymentStatus,
    });
    setPage(1);
  };

  return (
    <>
      <form
        onSubmit={handleSearchSubmit}
        className="bg-white border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3"
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Hợp đồng / khách hàng</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={contractSearch}
            onChange={(e) => setContractSearch(e.target.value)}
            placeholder="Mã HĐ, tên HĐ hoặc tên KH"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Căn hộ</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={apartmentSearch}
            onChange={(e) => setApartmentSearch(e.target.value)}
            placeholder="Mã căn hộ"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Kỳ thu phí</label>
          <input
            className="w-full border rounded px-3 py-2 text-sm"
            value={billingPeriod}
            onChange={(e) => setBillingPeriod(e.target.value)}
            placeholder="MM/YYYY"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Trạng thái</label>
          <select
            className="w-full border rounded px-3 py-2 text-sm"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value as PaymentStatusFilter)}
          >
            <option value="">Tất cả</option>
            <option value="UNPAID">Chưa thanh toán</option>
            <option value="PARTIAL">Thanh toán một phần</option>
            <option value="PAID">Đã thanh toán</option>
          </select>
        </div>
        <div className="flex items-end gap-2">
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded text-sm">
            Tìm kiếm
          </button>
          <button
            type="button"
            className="px-4 py-2 border rounded text-sm"
            onClick={() => {
              setContractSearch('');
              setApartmentSearch('');
              setBillingPeriod('');
              setPaymentStatus('');
              setAppliedFilters({
                contractSearch: '',
                apartmentSearch: '',
                billingPeriod: '',
                paymentStatus: '',
              });
              setPage(1);
            }}
          >
            Xóa lọc
          </button>
        </div>
      </form>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Mã HĐ</th>
              <th className="px-4 py-3 text-left">Khách hàng</th>
              <th className="px-4 py-3 text-left">Căn hộ</th>
              <th className="px-4 py-3 text-left">Kỳ thu phí</th>
              <th className="px-4 py-3 text-left">Số dòng</th>
              <th className="px-4 py-3 text-left">Số tiền</th>
              <th className="px-4 py-3 text-left">Đã thu</th>
              <th className="px-4 py-3 text-left">Trạng thái</th>
              <th className="px-4 py-3 text-left">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
            ) : invoiceGroups.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-500">Chưa có hóa đơn</td></tr>
            ) : invoiceGroups.map((group) => {
              const status = groupPaymentStatus(group);
              const unpaidIds = groupUnpaidInvoiceIds(group);
              const hasManual = group.invoices.some((inv) => needsManualAmount(inv));
              const expanded = expandedGroupKey === group.key;
              return (
                <React.Fragment key={group.key}>
                  <tr className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{group.contractCode}</td>
                    <td className="px-4 py-3">{group.customerName || '—'}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/apartments/${group.apartmentId}/details`}
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {group.apartmentCode}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{group.billingPeriod}</td>
                    <td className="px-4 py-3">{group.invoices.length}</td>
                    <td className="px-4 py-3">
                      {hasManual ? (
                        <span className="text-amber-700">Có dòng chưa nhập</span>
                      ) : (
                        formatVndAmount(groupTotalAmount(group))
                      )}
                    </td>
                    <td className="px-4 py-3">{formatVndAmount(groupPaidAmount(group))}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${PAYMENT_STATUS_CLASS[status]}`}>
                        {PAYMENT_STATUS_LABELS[status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-3">
                        {group.invoices.length > 1 ? (
                          <button
                            type="button"
                            className="text-cyan-700 hover:text-cyan-900"
                            onClick={() =>
                              setExpandedGroupKey((prev) => (prev === group.key ? null : group.key))
                            }
                          >
                            {expanded ? 'Thu gọn' : 'Chi tiết'}
                          </button>
                        ) : null}
                        {group.invoices.length === 1 && needsManualAmount(group.invoices[0]) ? (
                          <button
                            type="button"
                            onClick={() => openAmountModal(group.invoices[0])}
                            className="text-amber-700 hover:text-amber-900"
                          >
                            Nhập số tiền
                          </button>
                        ) : null}
                        {unpaidIds.length > 0 ? (
                          <button
                            type="button"
                            onClick={() =>
                              setPaymentModal({
                                apartmentId: group.apartmentId,
                                apartmentCode: group.apartmentCode,
                                invoiceIds: unpaidIds,
                                billingPeriod: group.billingPeriod,
                              })
                            }
                            className="text-green-600 hover:text-green-800"
                          >
                            Thanh toán kỳ
                          </button>
                        ) : null}
                        {group.invoices.length === 1 && groupCanDelete(group) ? (
                          <button
                            type="button"
                            onClick={() => handleDeleteInvoice(group.invoices[0])}
                            className="text-red-600 hover:text-red-800"
                          >
                            Xóa HĐ
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                  {expanded ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-2 bg-gray-50">
                        <ul className="space-y-2 text-sm text-gray-700">
                          {group.invoices.map((invoice) => (
                            <li key={invoice.id} className="flex flex-wrap items-center justify-between gap-2">
                              <span>
                                {formatVndAmount(contractInvoiceTotal(invoice))} ·{' '}
                                {PAYMENT_STATUS_LABELS[invoice.paymentStatus]} · hạn{' '}
                                {formatDateToDisplay(invoice.dueDate)}
                              </span>
                              <div className="flex gap-2">
                                {needsManualAmount(invoice) ? (
                                  <button
                                    type="button"
                                    className="text-amber-700"
                                    onClick={() => openAmountModal(invoice)}
                                  >
                                    Nhập số tiền
                                  </button>
                                ) : null}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {!loading && total > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-4 text-sm text-gray-600">
          <span>
            Tổng {total} hóa đơn · {invoices.length} dòng trên trang này
            {totalPages > 1 ? ` · Trang ${page}/${totalPages}` : ''}
          </span>
          {totalPages > 1 ? (
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Trước
              </button>
              <button
                type="button"
                className="px-3 py-1 border rounded disabled:opacity-50"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Sau
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {amountModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">Nhập số tiền hóa đơn</h3>
            <p className="text-sm text-gray-600 mb-4">
              {amountModal.contractCode} · {amountModal.apartmentCode} · Kỳ {amountModal.billingPeriod}
            </p>
            <label className="block text-sm font-medium mb-1">Số tiền (VNĐ)</label>
            <input
              type="text"
              inputMode="numeric"
              className="w-full border rounded px-3 py-2 mb-4"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value.replace(/[^\d]/g, ''))}
              onBlur={() => setAmountInput(formatContractAmountField(amountInput))}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 border rounded"
                onClick={closeAmountModal}
                disabled={savingAmount}
              >
                Hủy
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                onClick={saveInvoiceAmount}
                disabled={savingAmount}
              >
                {savingAmount ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {paymentModal ? (
        <ApartmentServiceFeePaymentModal
          isOpen
          apartmentId={paymentModal.apartmentId}
          apartmentCode={paymentModal.apartmentCode}
          initialSelectedFeeIds={paymentModal.invoiceIds}
          onClose={() => setPaymentModal(null)}
          onSuccess={() => {
            setPaymentModal(null);
            loadInvoices();
          }}
        />
      ) : null}
    </>
  );
};

export default ContractInvoiceListTab;
